import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import styles from './PedidosPage.module.css';
import AwsOrderDetailModal from './AwsOrderDetailModal';

interface AwsPedidoItem {
    id: number;
    cliente_nome: string;
    vendedor: string;
    data_emissao: string;
    status: number;
    valor_total: number;
    operacao_fiscal: string;
}

const formatCurrency = (value: any) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 'R$ 0,00';
    return numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pt-BR');
};

const getStatusBadge = (status: number) => {
    switch (status) {
        case 0: return <span style={{ padding: '4px 8px', borderRadius: '4px', background: '#e3f2fd', color: '#1976d2', fontWeight: 500, fontSize: '0.85em' }}>Em Aberto</span>;
        case 1: return <span style={{ padding: '4px 8px', borderRadius: '4px', background: '#ffebee', color: '#d32f2f', fontWeight: 500, fontSize: '0.85em' }}>Cancelado</span>;
        case 2: return <span style={{ padding: '4px 8px', borderRadius: '4px', background: '#e8f5e9', color: '#388e3c', fontWeight: 500, fontSize: '0.85em' }}>Finalizado</span>;
        case 3: return <span style={{ padding: '4px 8px', borderRadius: '4px', background: '#fff8e1', color: '#fbc02d', fontWeight: 500, fontSize: '0.85em' }}>Sincronizado</span>;
        default: return <span>Desconhecido</span>;
    }
};

const AwsPedidosPage: React.FC = () => {
    const { token } = useAuthStore();
    const navigate = useNavigate();
    const [orders, setOrders] = useState<AwsPedidoItem[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');
    const itemsPerPage = 20;

    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

    const fetchOrders = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const apiURL = process.env.REACT_APP_API_URL || '';
            const response = await axios.get(`${apiURL}/api/orders/aws`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { page: currentPage, limit: itemsPerPage, search: submittedSearch }
            });
            setOrders(response.data.orders || []);
            setTotalItems(response.data.totalItems || 0);
        } catch (err: any) {
            console.error("Erro ao carregar pedidos da AWS:", err);
            setError("Falha ao carregar os pedidos.");
            setOrders([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (token) fetchOrders();
    }, [token, currentPage, submittedSearch]);

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') { setSubmittedSearch(search); setCurrentPage(1); }
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
        <div className={styles.pageBackground}>
            <div className={styles.header} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h1 className={styles.title}>Pedidos</h1>
                <button
                    onClick={() => navigate('/aws-novo-pedido')}
                    style={{ padding: '10px 20px', background: '#57171e', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '0.95em' }}
                >
                    + Novo Pedido
                </button>
            </div>

            <div className={styles.filtersContainer} style={{ background: '#fff', padding: '15px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <input
                    type="text"
                    placeholder="Buscar por ID, Cliente ou Vendedor... (Pressione Enter)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    style={{ padding: '10px 15px', width: '100%', maxWidth: '400px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
                />
            </div>

            {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

            <div className={styles.tableContainer}>
                <table className={styles.ordersTable}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Cliente</th>
                            <th>Emissão</th>
                            <th>Valor Total</th>
                            <th>Vendedor</th>
                            <th>Operação</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>Carregando...</td></tr>
                        ) : orders.length > 0 ? (
                            orders.map((order) => (
                                <tr key={order.id} onClick={() => setSelectedOrderId(order.id)}
                                    style={{ cursor: 'pointer' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                                >
                                    <td><strong>{order.id}</strong></td>
                                    <td>{order.cliente_nome}</td>
                                    <td>{formatDate(order.data_emissao)}</td>
                                    <td style={{ fontWeight: 'bold' }}>{formatCurrency(order.valor_total)}</td>
                                    <td>{order.vendedor}</td>
                                    <td>{order.operacao_fiscal}</td>
                                    <td>{getStatusBadge(order.status)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>Nenhum pedido encontrado.</td></tr>
                        )}
                    </tbody>
                </table>

                {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', gap: '15px', alignItems: 'center' }}>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            style={{ padding: '8px 15px', borderRadius: '4px', border: 'none', background: currentPage === 1 ? '#eee' : '#57171e', color: currentPage === 1 ? '#999' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
                            Anterior
                        </button>
                        <span style={{ color: '#666' }}>Página {currentPage} de {totalPages}</span>
                        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            style={{ padding: '8px 15px', borderRadius: '4px', border: 'none', background: currentPage === totalPages ? '#eee' : '#57171e', color: currentPage === totalPages ? '#999' : '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>
                            Próxima
                        </button>
                    </div>
                )}
            </div>

            {selectedOrderId !== null && (
                <AwsOrderDetailModal
                    pedidoId={selectedOrderId}
                    onClose={() => setSelectedOrderId(null)}
                    onEdit={(id) => navigate(`/aws-editar-pedido/${id}`)}
                    onFinalize={fetchOrders}
                />
            )}
        </div>
    );
};

export default AwsPedidosPage;
